/**
 * Reveals its `[data-reveal]` descendants with a staggered fade-up once the element scrolls into view.
 *
 * Usage:
 *   <scroll-reveal class="my-grid">
 *     <div data-reveal style="--reveal-index: 0">…</div>
 *     <div data-reveal style="--reveal-index: 1">…</div>
 *   </scroll-reveal>
 *
 * Add the `reveal-children` attribute to reveal every direct child instead of marking items by hand
 * (used where children come from `content_for 'blocks'`).
 *
 * Styling lives in base.css (`scroll-reveal`). Items are only hidden after the element has
 * connected (`data-reveal-ready`), so content stays visible without JavaScript, in the theme
 * editor, and for users who prefer reduced motion.
 */
const REVEAL_ROOT_MARGIN = '0px 0px -10% 0px';
const REVEAL_THRESHOLD = 0.05;
const REVEAL_FALLBACK_DELAY = 2600;
const REVEAL_DONE_BASE = 700;
const REVEAL_DONE_STAGGER = 90;

class ScrollReveal extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #fallbackTimeout = null;

  connectedCallback() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (window.Shopify?.designMode || reducedMotion || !('IntersectionObserver' in window)) {
      this.reveal();
      return;
    }

    if (this.hasAttribute('reveal-children')) this.#markChildren();
    this.#indexItems();

    this.setAttribute('data-reveal-ready', '');

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.reveal();
      },
      { rootMargin: REVEAL_ROOT_MARGIN, threshold: REVEAL_THRESHOLD }
    );
    this.#observer.observe(this);

    // Guarantees content is never stuck hidden (e.g. when the element is taller than the viewport).
    this.#fallbackTimeout = window.setTimeout(() => this.reveal(), REVEAL_FALLBACK_DELAY);
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  reveal() {
    if (this.hasAttribute('data-revealed')) return;

    this.setAttribute('data-revealed', '');
    this.#cleanup();

    // Drop the stagger delay once every item has finished animating so hover transitions stay snappy.
    const items = this.querySelectorAll('[data-reveal]').length;
    window.setTimeout(() => this.setAttribute('data-reveal-done', ''), REVEAL_DONE_BASE + items * REVEAL_DONE_STAGGER);
  }

  /** Gives every `[data-reveal]` item without an explicit `--reveal-index` its position, for the stagger. */
  #indexItems() {
    this.querySelectorAll('[data-reveal]').forEach((item, index) => {
      if (item instanceof HTMLElement && !item.style.getPropertyValue('--reveal-index')) {
        item.style.setProperty('--reveal-index', String(index));
      }
    });
  }

  #markChildren() {
    Array.from(this.children).forEach((child, index) => {
      if (!(child instanceof HTMLElement) || child.hasAttribute('data-reveal')) return;
      child.setAttribute('data-reveal', '');
      child.style.setProperty('--reveal-index', String(index));
    });
  }

  #cleanup() {
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#fallbackTimeout) window.clearTimeout(this.#fallbackTimeout);
    this.#fallbackTimeout = null;
  }
}

if (!customElements.get('scroll-reveal')) {
  customElements.define('scroll-reveal', ScrollReveal);
}
